<template>
  <div class="chat-messages" ref="chatContainer">
    <div v-if="messages.length === 0" class="empty-state">
      <div class="empty-state-content">
        <h3>Start a new conversation</h3>
        <p>Type a message to begin.</p>
      </div>
    </div>
    <div v-else v-for="msg in messages" :key="msg.id" class="chat-message"
         :class="['chat-message', msg.role === 'user' ? 'chat-message--user' : 'chat-message--assistant']"
    >
      <div v-if="msg.role === 'assistant'" class="chat-avatar assistant">
        <Icon name="chat-bot" size="16" class="icon"/>
      </div>
      <div class="chat-bubble" :class="msg.role">
        <p class="text" v-html="getDisplayText(msg)"></p>
        <span class="timestamp">{{ formatTimestamp(msg.timestamp) }}</span>
      </div>
      <div v-if="msg.role === 'user'" class="chat-avatar user">
        <div class="avatar-div" :style="{backgroundImage: `url(${profile.photo_url}`}"/>
      </div>
    </div>
    <div v-if="isThinking && !shouldAnimate" class="chat-message chat-message--assistant">
      <div class="chat-avatar assistant">
        <Icon name="chat-bot" size="16" class="icon"/>
      </div>
      <div class="typing-dots">
        <span class="dot"/>
        <span class="dot"/>
        <span class="dot"/>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
  import { defineComponent } from 'vue'
  import Icon from '@/components/Icon/Icon.vue'
  import UserMixin from '@/util/UserMixin'
  import moment from 'moment'
  import { Message } from '@/Domain/ConversationalAgent/Types/types'

  export default defineComponent({
    name: 'ConversationalAgentChat',
    components: { Icon },
    mixins: [UserMixin],
    props: {
      messages: {
        type: Array as () => Message[],
        required: true
      },
      isThinking: {
        type: Boolean,
        default: false
      }
    },
    emits: ['typing-complete', 'scroll-down'],
    data() {
      return {
        currentLength: 0,
        typingInterval: null as number | null,
        shouldAnimate: false
      }
    },
    methods: {
      parseMarkdownLinksToHtml(text: string): string {
        const markdownLinkRegex = /\[([^\]]+)]\((https?:\/\/[^\s)]+)\)/g;
        return text.replace(markdownLinkRegex, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>');
      },
      formatTimestamp(timestamp: string | undefined) {
        const momentDate = moment(timestamp);
        return momentDate.isValid() ? momentDate.fromNow() : 'Just now';
      },
      getDisplayText(message: Message): string {
        if (message.role === 'user') {
          return this.parseMarkdownLinksToHtml(message.message);
        }

        // Only animate if this is the last message, it's from the assistant, and it's marked for typing
        if (this.shouldAnimate &&
          message === this.messages[this.messages.length - 1] &&
          message.role === 'assistant' &&
          message.isTyping) {
          // Get first two sentences
          const sentences = message.message.match(/[^.!?]+[.!?]+/g) || [];
          const firstTwoSentences = sentences.slice(0, 2).join('');

          if (this.currentLength <= firstTwoSentences.length) {
            // Animate first two sentences
            return this.parseMarkdownLinksToHtml(message.message.substring(0, this.currentLength));
          } else {
            // Show rest of the message with animation
            return this.parseMarkdownLinksToHtml(message.message);
          }
        }

        return this.parseMarkdownLinksToHtml(message.message);
      }
    },
    watch: {
      messages: {
        handler(newMessages, oldMessages) {
          const lastMessage = newMessages[newMessages.length - 1];

          // Only animate if we have a new message, it's from the assistant, and it's marked for typing
          if (lastMessage &&
            lastMessage.role === 'assistant' &&
            lastMessage.isTyping) {
            // Get first two sentences
            const sentences = lastMessage.message.match(/[^.!?]+[.!?]+/g) || [];
            const firstTwoSentences = sentences.slice(0, 2).join('');

            // Clear any existing interval
            if (this.typingInterval) {
              clearInterval(this.typingInterval);
            }

            // Start new animation
            this.currentLength = 0;
            this.shouldAnimate = true;
            this.typingInterval = window.setInterval(() => {
              if (this.currentLength <= firstTwoSentences.length) {
                // Animate first two sentences
                this.currentLength++;
                this.$emit('scroll-down');
              } else {
                // Show rest of the message
                if (this.typingInterval) {
                  clearInterval(this.typingInterval);
                  this.typingInterval = null;
                  this.shouldAnimate = false;
                  this.$emit('typing-complete');
                  this.$emit('scroll-down');
                }
              }
            }, 30);
          }
        },
        deep: true
      }
    },
    beforeUnmount() {
      if (this.typingInterval) {
        clearInterval(this.typingInterval);
      }
    }
  })
</script>

<style scoped lang="scss">
  .chat-messages {
    padding: 0.2rem;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    height: 100%;
    flex: 1;

    /* Hide scrollbar for Chrome, Safari and Opera */
    &::-webkit-scrollbar {
      display: none;
    }

    /* Hide scrollbar for IE, Edge and Firefox */
    -ms-overflow-style: none; /* IE and Edge */
    scrollbar-width: none; /* Firefox */
  }

  .chat-message {
    display: flex;
    align-items: flex-end;
    gap: 0.75rem;
    margin: 0.5rem 0;
    padding: 0.2rem;
    animation: fadeIn 0.3s ease-in-out;

    &--user {
      justify-content: flex-end;
    }

    &--assistant {
      justify-content: flex-start;
    }

    .chat-avatar {
      width: 2rem;
      height: 2rem;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      animation: fadeIn 0.3s ease-in-out;

      &.assistant {
        background-color: #D9CEFF;
      }

      &.user {
        .avatar-div {
          width: 30px;
          height: 30px;
          background-size: cover;
          background-repeat: no-repeat;
          border-radius: 50%;
        }
      }

      .icon {
        color: white;
      }
    }

    .chat-bubble {
      max-width: 70%;
      padding: 0.75rem;
      border-radius: 0.5rem;
      position: relative;
      animation: fadeIn 0.3s ease-in-out;

      &.user {
        background-color: #F3F1FF;
        color: #111827;
      }

      &.assistant {
        color: #111827;
      }

      .text {
        white-space: pre-wrap;
        text-overflow: ellipsis;
        overflow: hidden;
        transition: all 0.3s ease-in-out;
      }

      .timestamp {
        font-size: 0.75rem;
        display: block;
        color: #6b7280;
      }

      &.assistant .timestamp {
        color: #6b7280;
      }
    }
  }

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .typing-dots {
    display: flex;
    gap: 0.25rem;
    animation: fadeIn 0.3s ease-in-out;

    .dot {
      width: 0.5rem;
      height: 0.5rem;
      background-color: #9ca3af;
      border-radius: 9999px;
      animation: bounce 0.6s infinite alternate;
    }

    .dot:nth-child(2) {
      animation-delay: 0.1s;
    }

    .dot:nth-child(3) {
      animation-delay: 0.2s;
    }
  }

  @keyframes bounce {
    0% {
      transform: translateY(0);
    }
    100% {
      transform: translateY(-4px);
    }
  }

  .empty-state {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    padding: 2rem;
    text-align: center;
    color: #6B7280;

    .empty-state-content {
      max-width: 400px;

      h3 {
        font-size: 1.25rem;
        font-weight: 600;
        margin-bottom: 0.5rem;
        color: #111827;
      }

      p {
        font-size: 1rem;
        line-height: 1.5;
      }
    }
  }
</style>
